/**
 * Centralized mapping between official API-Football event (type + detail)
 * and the VerseSport UI (icon + label i18n key + accent color).
 *
 * Adding a new event type in the future MUST only require appending a
 * matcher here — never editing renderers.
 */
import type { ProviderMatchEvent } from "@/lib/sports/providers/types";

export type MatchEventCategory =
  | "goal"
  | "penalty_goal"
  | "penalty_missed"
  | "goal_disallowed"
  | "own_goal"
  | "yellow_card"
  | "red_card"
  | "second_yellow"
  | "substitution"
  | "var_review"
  | "var_goal"
  | "var_cancelled"
  | "half_time"
  | "second_half"
  | "extra_time"
  | "penalty_shootout"
  | "full_time"
  | "interruption"
  | "unknown";

export interface MatchEventPresentation {
  category: MatchEventCategory;
  /** Emoji is the neutral, framework-free VerseSport icon. */
  icon: string;
  /** i18n key resolved via useLanguage().t. */
  labelKey: string;
  /** Semantic accent (uses oklch tokens). */
  color: string;
}

function has(needle: string, hay: string): boolean {
  return hay.toLowerCase().includes(needle.toLowerCase());
}

/**
 * Classify a normalized provider event into a UI category.
 * Pure, deterministic — no fabrication, no randomness.
 */
export function classifyEvent(e: ProviderMatchEvent): MatchEventCategory {
  const type = (e.type ?? "").toLowerCase();
  const detail = (e.detail ?? "").toLowerCase();

  if (type === "goal") {
    if (has("own", detail)) return "own_goal";
    if (has("penalty", detail)) return "penalty_goal";
    if (has("missed", detail)) return "penalty_missed";
    if (has("disallowed", detail) || has("cancelled", detail)) return "goal_disallowed";
    return "goal";
  }
  if (type === "card") {
    if (has("second yellow", detail)) return "second_yellow";
    if (has("red", detail)) return "red_card";
    if (has("yellow", detail)) return "yellow_card";
    return "yellow_card";
  }
  if (type === "subst" || type === "substitution") return "substitution";
  if (type === "var") {
    if (has("goal cancelled", detail) || has("no goal", detail)) return "var_cancelled";
    if (has("goal awarded", detail)) return "var_goal";
    return "var_review";
  }
  if (type === "penalty" || has("penalty shootout", detail)) return "penalty_shootout";
  if (has("half-time", detail) || detail === "ht") return "half_time";
  if (has("second half", detail)) return "second_half";
  if (has("extra time", detail)) return "extra_time";
  if (has("full time", detail) || detail === "ft") return "full_time";
  if (has("interruption", detail) || has("suspended", detail)) return "interruption";
  return "unknown";
}

export const PRESENTATION: Record<MatchEventCategory, MatchEventPresentation> = {
  goal:            { category: "goal",            icon: "⚽", labelKey: "mev_goal",            color: "oklch(0.7 0.22 27)" },
  penalty_goal:    { category: "penalty_goal",    icon: "⚽", labelKey: "mev_penalty_goal",    color: "oklch(0.7 0.22 27)" },
  penalty_missed:  { category: "penalty_missed",  icon: "❌", labelKey: "mev_penalty_missed",  color: "oklch(0.65 0.27 27)" },
  goal_disallowed: { category: "goal_disallowed", icon: "🚫", labelKey: "mev_goal_disallowed", color: "oklch(0.65 0.27 27)" },
  own_goal:        { category: "own_goal",        icon: "⚽", labelKey: "mev_own_goal",        color: "oklch(0.7 0.22 27)" },
  yellow_card:     { category: "yellow_card",     icon: "🟨", labelKey: "mev_yellow_card",     color: "oklch(0.88 0.18 95)" },
  red_card:        { category: "red_card",        icon: "🟥", labelKey: "mev_red_card",        color: "oklch(0.65 0.27 27)" },
  second_yellow:   { category: "second_yellow",   icon: "🟨🟥", labelKey: "mev_second_yellow", color: "oklch(0.72 0.24 40)" },
  substitution:    { category: "substitution",    icon: "🔁", labelKey: "mev_substitution",    color: "oklch(0.65 0.04 270)" },
  var_review:      { category: "var_review",      icon: "📺", labelKey: "mev_var_review",      color: "oklch(0.7 0.18 230)" },
  var_goal:        { category: "var_goal",        icon: "📺", labelKey: "mev_var_goal",        color: "oklch(0.82 0.24 145)" },
  var_cancelled:   { category: "var_cancelled",   icon: "📺", labelKey: "mev_var_cancelled",   color: "oklch(0.65 0.27 27)" },
  half_time:       { category: "half_time",       icon: "⏱", labelKey: "mev_half_time",       color: "oklch(0.75 0.04 270)" },
  second_half:     { category: "second_half",     icon: "▶",  labelKey: "mev_second_half",     color: "oklch(0.75 0.04 270)" },
  extra_time:      { category: "extra_time",      icon: "⏱", labelKey: "mev_extra_time",      color: "oklch(0.75 0.04 270)" },
  penalty_shootout:{ category: "penalty_shootout",icon: "🎯", labelKey: "mev_penalty_shootout",color: "oklch(0.72 0.24 40)" },
  full_time:       { category: "full_time",       icon: "🏁", labelKey: "mev_full_time",       color: "oklch(0.75 0.04 270)" },
  interruption:    { category: "interruption",    icon: "⚠️", labelKey: "mev_interruption",    color: "oklch(0.72 0.24 40)" },
  unknown:         { category: "unknown",         icon: "•",  labelKey: "mev_unknown",         color: "oklch(0.65 0.04 270)" },
};

export function presentationFor(e: ProviderMatchEvent): MatchEventPresentation {
  return PRESENTATION[classifyEvent(e)];
}